import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MediaPicker } from "@/components/admin/MediaPicker";
import { FONT_OPTIONS } from "@/lib/theme";

const selectCls =
  "h-10 w-full rounded-sm border border-zinc-300 bg-white px-3 text-sm text-zinc-800 focus:outline-none focus:ring-1 focus:ring-[#FF6B00]";

const ColorField = ({ label, value, onChange, testid }) => (
  <div>
    <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">{label}</Label>
    <div className="flex gap-2 items-center">
      <input
        type="color"
        value={value || "#000000"}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-14 rounded-sm border border-zinc-300 bg-white cursor-pointer"
        data-testid={`${testid}-picker`}
      />
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="bg-white border-zinc-300 text-zinc-800 rounded-sm font-mono text-xs" data-testid={`${testid}-input`} />
    </div>
  </div>
);

export default function AdminDesign() {
  const [theme, setTheme] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const r = await api.get("/admin/content/theme");
      setTheme(r.data.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (!theme) {
    return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" /></div>;
  }

  const set = (k) => (v) => setTheme((t) => ({ ...t, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/content/theme", { data: theme });
      toast.success("Design saved. Refresh the public site to see changes.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-design">
      <h1 className="font-display text-2xl font-black uppercase text-zinc-900 mb-1">Design & Branding</h1>
      <p className="text-sm text-zinc-500 mb-6">Controlled design tokens — no CSS editing required. Applies across the public site.</p>

      <form onSubmit={save} className="bg-white border border-zinc-200 p-6 space-y-6 max-w-3xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500 block mb-1.5">Logo</Label>
            <MediaPicker value={theme.logo_url} onChange={set("logo_url")} testid="theme-logo" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500 block mb-1.5">Favicon</Label>
            <MediaPicker value={theme.favicon_url} onChange={set("favicon_url")} testid="theme-favicon" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <ColorField label="Primary Brand Colour" value={theme.primary_color} onChange={set("primary_color")} testid="theme-primary" />
          <ColorField label="Accent Colour" value={theme.accent_color} onChange={set("accent_color")} testid="theme-accent" />
          <ColorField label="Background Colour" value={theme.background_color} onChange={set("background_color")} testid="theme-background" />
          <ColorField label="Text Colour" value={theme.text_color} onChange={set("text_color")} testid="theme-text" />
          <ColorField label="Secondary Colour" value={theme.secondary_color} onChange={set("secondary_color")} testid="theme-secondary" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Heading Font</Label>
            <select value={theme.heading_font || "Chivo"} onChange={(e) => set("heading_font")(e.target.value)} className={selectCls} data-testid="theme-heading-font">
              {FONT_OPTIONS.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Body Font</Label>
            <select value={theme.body_font || "Inter"} onChange={(e) => set("body_font")(e.target.value)} className={selectCls} data-testid="theme-body-font">
              {FONT_OPTIONS.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Button Style</Label>
            <select value={theme.button_style || "square"} onChange={(e) => set("button_style")(e.target.value)} className={selectCls} data-testid="theme-button-style">
              <option value="square">Square</option>
              <option value="rounded">Rounded</option>
              <option value="pill">Pill</option>
            </select>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Border Radius</Label>
            <select value={theme.border_radius || "sm"} onChange={(e) => set("border_radius")(e.target.value)} className={selectCls} data-testid="theme-border-radius">
              <option value="none">None</option>
              <option value="sm">Small</option>
              <option value="md">Medium</option>
              <option value="lg">Large</option>
            </select>
          </div>
        </div>

        <Button type="submit" disabled={saving} className="bg-[#FF6B00] hover:bg-[#FF8533] text-black font-bold uppercase tracking-wider rounded-sm" data-testid="theme-save-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Design"}
        </Button>
      </form>
    </div>
  );
}
